import { zodResolver } from "@hookform/resolvers/zod";
import { TFunction } from "i18next";
import React, { useEffect, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { z } from "zod";
import useLoginMutation from "../../../api/auth/login";
import {
  GeneralButton,
  GeneralInput,
  Loader,
  PhoneNumberInput,
} from "../../../components";
import AuthLayout from "../../../Layout/AuthLayout";
import { RootState } from "../../../redux/store";
import PrivacyPolicyModal from "../../PrivacyPolicy/PrivacyPolicy";
interface LoginFormInputs {
  phone: string;
  Password: string;
}

interface LoginRequest {
  Identification: string;
  Password: string;
}

const Login: React.FC = () => {
  const validate = useSelector((state: RootState) => state.phValidation);
  const { t, i18n } = useTranslation();
  const lan = i18n.language?.slice(0, 2);

  const { pathname } = useLocation();

  const isPrivacyOpen = pathname?.includes(`/privacy_policy`);

  const createLoginSchema = (t: TFunction) => {
    return z.object({
      phone: z
        .string()
        .min(1, t("phone_number_required"))
        .superRefine((value, ctx) => {
          if (!validate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("validation_configuration_error"),
            });
            return;
          }
          const phoneNumber = value.replace(/[^0-9]/g, "");
          const countryStart = validate.Start.toString();
          const countryLength = validate.Length;
          const startWith0 = `0${countryStart}`;
          const startWithCountryStart = `${countryStart}`;

          if (phoneNumber.startsWith(startWith0)) {
            if (phoneNumber.length !== countryLength + 1) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("phone_number_must_be_and_start_with", {
                  length: countryLength + 1,
                  start: startWith0,
                }),
              });
            }
          } else if (phoneNumber.startsWith(startWithCountryStart)) {
            if (phoneNumber.length !== countryLength) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("phone_number_must_be_and_start_with", {
                  length: countryLength,
                  start: startWithCountryStart,
                }),
              });
            }
          } else {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("phone_number_must_start_with", {
                start1: startWithCountryStart,
                start2: startWith0,
              }),
            });
          }
        }),
      Password: z.string().min(1, t("password_required")),
    });
  };

  const validationSchema = useMemo(() => createLoginSchema(t), [t, validate]);

  const methods = useForm<LoginFormInputs>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      phone: "",
      // Password: "",
    },
  });

  const {
    handleSubmit,
    trigger,
    formState: { isSubmitted },
  } = methods;

  const { mutate, isPending } = useLoginMutation();

  useEffect(() => {
    if (isSubmitted) {
      trigger();
    }
  }, [t, trigger, isSubmitted]);

  const onSubmit: SubmitHandler<LoginFormInputs> = (data) => {
    let phoneNumber = data.phone;
    if (phoneNumber.startsWith("0")) {
      phoneNumber = phoneNumber.substring(1);
    }

    const requestData: LoginRequest = {
      Identification: phoneNumber,
      Password: data.Password,
    };

    mutate(requestData);
  };

  return (
    <>
      <FormProvider {...methods}>
        <AuthLayout title={t("welcome_msg")}>
          <form
            className="flex w-full flex-col gap-5"
            onSubmit={handleSubmit(onSubmit)}
          >
            <PhoneNumberInput />
            <GeneralInput
              name="Password"
              type="password"
              label={t("password")}
              placeholder={t("password")}
              required={true}
            />

            <div className="-mt-2 flex justify-end">
              <Link
                to="/forgot-password"
                className="hover:text-600 text-base font-bold underline"
              >
                {t("forgot_password_question")}
              </Link>
            </div>
            <GeneralButton
              type="submit"
              title={t("login")}
              color="blue"
              block
              disabled={isPending}
            />
            {isPending && <Loader />}
          </form>
          <div className="mt-4 text-center">
            <p className="text-base">
              {t("new_to_domecare")}
              <Link
                to="/sign-up"
                className="text-secondary ms-2 font-bold underline"
              >
                {t("create_account")}
              </Link>
            </p>
          </div>
        </AuthLayout>
      </FormProvider>
      {isPrivacyOpen && <PrivacyPolicyModal />}
    </>
  );
};

export default Login;
